import { z } from "zod";

/**
 * Shared DTO cho Admin audit-log viewer (US-AE9.F4-0031 · @UC9083 · @gate pii) — trình ĐỌC toàn hệ
 * bảng `audit_log` (append-only). Một nguồn sự thật shape cho api (S01) + admin web (S03) + e2e (S04).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - `audit_log` APPEND-ONLY (G2) — CHỈ có endpoint READ; contract KHÔNG có shape mutate.
 * - `diff` đã redacted-at-write (redactDiff: drop password/token/secret, mask email/phone). Viewer
 *   CHỈ render. `diff` nullable → redaction THEO VAI (vai đọc-thấp SUPPORT/READ_ONLY server trả
 *   null, defense-in-depth least-privilege). Contract KHÔNG ép lộ PII thô.
 * - `ip` nullable → cũng ẩn cho vai đọc-thấp.
 * - `result` = "pass" cho mọi dòng persisted (audit ghi enforce-before-commit — hành động fail thì
 *   rollback, không persist). KHÔNG cột DB `result`; derive ở server (DR-2).
 * - `.strict()` — KHÔNG khai báo field lạ; reject tại biên (chống leak).
 * - Cursor opaque string — đối xứng common/pagination/cursor (keyset createdAt+id).
 * - Filter whitelist: actor(uuid)/action/entity/dateFrom/dateTo/result — reject field lạ tại biên.
 */

/** Max page-size list audit-log. Default (20) do backend áp khi vắng. */
export const ADMIN_AUDIT_LOG_MAX_LIMIT = 100;

/** Kết quả một dòng audit — persisted rows luôn "pass" (DR-2). Taxonomy đóng. */
export const auditResultSchema = z.enum(["pass"]);
export type AuditResult = z.infer<typeof auditResultSchema>;

/**
 * Một dòng audit-log: actor → action → entity(+id) → diff(redacted) → ip → createdAt.
 * `diff`/`ip` nullable (redaction theo vai). `diff` là JSON đã redacted (unknown — có thể object
 * {before,after} hoặc bất kỳ shape nào đã che PII/secret).
 */
export const auditLogItemSchema = z
  .object({
    id: z.string(),
    /** admin/user gây hành động (uuid — surface hiển thị ref/chip; null nếu không truy). */
    actorUserId: z.string().nullable(),
    /**
     * Display-name của actor đã resolve server-side (T01 · @UC9135): USER =
     * username(social)→email→phone; ADMIN = email. null khi actorUserId null HOẶC không resolve được
     * (mồ côi) → UI fallback uuid-slice. KHÔNG phơi thêm PII ngoài name hiển thị.
     */
    actorName: z.string().nullable(),
    action: z.string(),
    entity: z.string().nullable(),
    entityId: z.string().nullable(),
    /**
     * Display-name của ĐỐI TƯỢNG đã resolve server-side (T01b · @UC9135): entity=admin_member → email;
     * user → username(social)→email→phone; group/fund → name. null khi không resolve được (mồ côi/
     * entity không phải loại có tên) → UI ẩn chip id (không hiển thị #uuid vô nghĩa). KHÔNG phơi thêm PII.
     */
    entityName: z.string().nullable(),
    /** before/after ĐÃ redacted-at-write; null cho vai đọc-thấp (redaction theo vai — DR-3). */
    diff: z.unknown().nullable(),
    /** IP hành động; null cho vai đọc-thấp (least-privilege). */
    ip: z.string().nullable(),
    /** persisted ⇒ luôn "pass" (enforce-before-commit — DR-2). */
    result: auditResultSchema,
    createdAt: z.string(),
  })
  .strict();
export type AuditLogItem = z.infer<typeof auditLogItemSchema>;

/** Envelope list audit-log (cursor pagination) — đối xứng repo {data, pageInfo}. */
export const auditLogListResponseSchema = z
  .object({
    data: z.array(auditLogItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AuditLogListResponse = z.infer<typeof auditLogListResponseSchema>;

/**
 * Bộ lọc query audit-log (whitelist) — validate tại biên. actor(uuid)/action/entity là exact match;
 * dateFrom/dateTo là ISO; result đóng ("pass"). from ≤ to (superRefine). KHÔNG field lạ (.strict()).
 */
export const auditLogFilterSchema = z
  .object({
    actor: z.string().uuid().optional(),
    action: z.string().max(128).optional(),
    entity: z.string().max(128).optional(),
    dateFrom: z.string().datetime().optional(),
    dateTo: z.string().datetime().optional(),
    result: auditResultSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.dateFrom && v.dateTo && new Date(v.dateFrom) > new Date(v.dateTo)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "dateFrom must be <= dateTo",
        path: ["dateFrom"],
      });
    }
  });
export type AuditLogFilter = z.infer<typeof auditLogFilterSchema>;
